/**
 * Chart date navigation composable.
 *
 * Manages year/month/day view state and navigation for the BarChart component.
 */

import { ref, computed, watch } from 'vue'
import {
  addDays,
  addMonths,
  addYears,
  startOfMonth,
  startOfYear,
  startOfToday,
} from 'date-fns'

export function useChartNavigation(emit) {
  const today = startOfToday()
  // Default to yesterday if Amsterdam time is before 14:00 (data retrieval runs ~13:30)
  const nowAmsterdam = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }))
  const defaultDate = nowAmsterdam.getHours() < 14 ? addDays(today, -1) : today
  const viewYear = ref(defaultDate.getFullYear())
  const viewMonth = ref(defaultDate.getMonth())
  const viewDay = ref(defaultDate.getDate())
  const level = ref('day')

  const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']

  const levelLabel = computed(() => {
    if (level.value === 'year') return 'Jaaroverzicht \u2013 klik op maand voor detail'
    if (level.value === 'month') return 'Maandoverzicht \u2013 klik op dag voor detail'
    return 'Dagoverzicht \u2013 Wh per kwartier'
  })

  // Half-open range: `from` is inclusive, `until` exclusive. The aggregate
  // endpoints filter on `date_time < date_till` (see daily-aggregates and
  // month-aggregates in eleena-home), so `until` has to be the first day AFTER
  // the visible period. With an inclusive end date every period silently lost
  // its last day: 28 or 29 februari, 31 maart, 30 april, and so on.
  const dateRange = computed(() => {
    if (level.value === 'year') {
      const start = startOfYear(new Date(viewYear.value, 0, 1))
      return { from: start, until: addYears(start, 1) }
    }
    if (level.value === 'month') {
      const start = startOfMonth(new Date(viewYear.value, viewMonth.value, 1))
      return { from: start, until: addMonths(start, 1) }
    }
    const start = new Date(viewYear.value, viewMonth.value, viewDay.value)
    return { from: start, until: addDays(start, 1) }
  })

  const setLevel = (newLevel) => { level.value = newLevel }

  const navigatePrev = () => {
    if (level.value === 'year') {
      viewYear.value--
    } else if (level.value === 'month') {
      const d = addMonths(new Date(viewYear.value, viewMonth.value, 1), -1)
      viewYear.value = d.getFullYear()
      viewMonth.value = d.getMonth()
    } else {
      const d = addDays(new Date(viewYear.value, viewMonth.value, viewDay.value), -1)
      viewYear.value = d.getFullYear()
      viewMonth.value = d.getMonth()
      viewDay.value = d.getDate()
    }
  }

  const navigateNext = () => {
    if (level.value === 'year') {
      viewYear.value++
    } else if (level.value === 'month') {
      const d = addMonths(new Date(viewYear.value, viewMonth.value, 1), 1)
      viewYear.value = d.getFullYear()
      viewMonth.value = d.getMonth()
    } else {
      const d = addDays(new Date(viewYear.value, viewMonth.value, viewDay.value), 1)
      viewYear.value = d.getFullYear()
      viewMonth.value = d.getMonth()
      viewDay.value = d.getDate()
    }
  }

  const goToToday = () => {
    const now = startOfToday()
    viewYear.value = now.getFullYear()
    viewMonth.value = now.getMonth()
    viewDay.value = now.getDate()
    level.value = 'day'
  }

  const navigateToDay = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number)
    viewYear.value = y
    viewMonth.value = m - 1
    viewDay.value = d
    level.value = 'day'
  }

  // Emit date changes to parent (for voltage section sync)
  watch(
    [() => viewYear.value, () => viewMonth.value, () => viewDay.value],
    () => {
      emit('view-date-change', new Date(viewYear.value, viewMonth.value, viewDay.value))
    },
    { immediate: true }
  )

  return {
    today,
    viewYear,
    viewMonth,
    viewDay,
    level,
    monthNames,
    levelLabel,
    dateRange,
    setLevel,
    navigatePrev,
    navigateNext,
    goToToday,
    navigateToDay,
  }
}
